import { useEffect } from "react";
import { Copyright, User, ShieldOff, Ban, Globe, Edit3, Mail } from "lucide-react";
import LegalPage from "@/components/legal/LegalPage";

const TERMS = [
  {
    icon: Copyright,
    title: "Copyright Ownership",
    body: "All content, text, graphics, charts, illustrations, worksheets, templates, videos, and materials within The 9-to-5 Exit Blueprint (Foundation Guide and Action Bundle) are the exclusive property of Trade Flux. All rights are reserved. The purchase of this guide grants you a limited license — not ownership — of the material.",
  },
  {
    icon: User,
    title: "Personal Use Only",
    body: "Your purchase grants you a single-user, non-transferable, non-exclusive license to access and use the guide for your own personal educational purposes. You may print or store one copy for personal reference. Any use beyond personal use is strictly prohibited.",
  },
  {
    icon: ShieldOff,
    title: "No Redistribution or File Sharing",
    body: "You may not copy, forward, share, distribute, transmit, or otherwise make the guide (or any portion of it) available to any other person, whether or not for compensation. This includes sharing via email, cloud drives, messaging apps, torrents, or peer-to-peer networks.",
  },
  {
    icon: Ban,
    title: "No Resale",
    body: "You may not sell, resell, sublicense, lease, rent, or otherwise commercially exploit the guide or any part of it in any form or by any means. The license you receive is strictly for personal, non-commercial use.",
  },
  {
    icon: Globe,
    title: "No Public Uploads",
    body: "You may not upload, post, host, publish, or otherwise make the guide available on any public website, blog, forum, social media platform, learning platform, file-sharing service, or torrent tracker.",
  },
  {
    icon: Edit3,
    title: "No Modification or Derivative Works",
    body: "You may not modify, adapt, translate, remix, repackage, rebrand, or create derivative works based on the guide, and you may not represent any portion of the material as your own original work. Reselling modified versions of the material — under any name — is prohibited.",
  },
];

export default function LicenseAgreement() {
  useEffect(() => {
    document.title = "License Agreement — Trade Flux";
  }, []);

  return (
    <LegalPage
      eyebrow="License Agreement"
      title="License Agreement"
      updated="2026"
    >
      <p className="text-white/70 text-base md:text-lg">
        By purchasing and downloading <strong className="text-white">The 9-to-5 Exit Blueprint</strong>{" "}
        (Foundation Guide and/or Action Bundle), you agree to the terms of this
        License Agreement. Please read carefully.
      </p>

      <div className="not-prose mt-10 grid gap-4">
        {TERMS.map((t, i) => (
          <div
            key={t.title}
            className="rounded-2xl border border-white/10 bg-[#0c0c0c] p-6 hover:border-emerald-500/30 transition-colors"
          >
            <div className="flex items-start gap-4">
              <span className="shrink-0 w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/25 grid place-items-center">
                <t.icon className="w-4 h-4 text-emerald-400" />
              </span>
              <div>
                <div className="text-[10px] font-display tracking-[0.25em] uppercase text-white/40 mb-1">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h3 className="font-display font-semibold text-white text-lg tracking-tight">
                  {t.title}
                </h3>
                <p className="mt-2 text-white/65 text-sm md:text-[15px] leading-relaxed">
                  {t.body}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-10 rounded-2xl border border-emerald-500/25 bg-emerald-500/5 p-6">
        <div className="flex items-start gap-4">
          <span className="shrink-0 w-10 h-10 rounded-xl bg-emerald-500/15 border border-emerald-500/30 grid place-items-center">
            <Mail className="w-4 h-4 text-emerald-400" />
          </span>
          <div>
            <h3 className="font-display font-semibold text-white text-lg tracking-tight">
              Licensing Questions
            </h3>
            <p className="mt-2 text-white/70 text-sm md:text-[15px] leading-relaxed">
              For questions about this License Agreement, permission requests, or
              to report unauthorized use of the material, please contact us at{" "}
              <a
                href="mailto:support@trade-flux.com"
                className="text-emerald-300 hover:text-emerald-200 underline underline-offset-4"
              >
                support@trade-flux.com
              </a>
              .
            </p>
          </div>
        </div>
      </div>

      <p className="mt-10 text-xs text-white/40 leading-relaxed">
        Violation of any terms of this License Agreement automatically terminates
        your license, without refund, and may subject you to civil and/or criminal
        liability under applicable copyright and intellectual property laws.
      </p>
    </LegalPage>
  );
}
